import { useRef, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Box,
  Typography,
  AccordionSummary,
  AccordionDetails,
  Accordion,
} from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';

import AccordionSectionForm from './AccordionSectionForm';
import CopySectionForm from './CopySectionForm';
import {
  AccordionContentDto,
  CopyContentDto,
  SectionContentDto,
} from '@act/shared/data-transfer-objects';

interface DraggableSectionProps {
  section: SectionContentDto;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onChange: (index: number, section: SectionContentDto) => void;
  onDeleteClick: (sectionIndex: number) => void;
  onDeleteEntry: (sectionIndex: number, entryIndex: number) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const DraggableSection = ({
  section,
  index,
  onMove,
  onChange,
  onDeleteClick,
  onDeleteEntry,
}: DraggableSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: string | symbol | null }
  >({
    accept: 'section',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'section',
    item: () => ({ index, id: `section-${index}`, type: 'section' }),
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDeleteClick(index);
  };

  const getSectionIcon = useCallback(() => {
    if (!['accordion', 'copy'].includes(section.type)) {
      return null;
    }

    return section.type === 'accordion' ? (
      <ExpandCircleDownOutlinedIcon />
    ) : (
      <NotesOutlinedIcon />
    );
  }, [section]);

  const sectionIcon = getSectionIcon();

  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId}>
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              margin: '0',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              cursor: 'move',
            }}
          >
            <DragIndicatorIcon
              sx={{
                marginRight: '8px',
                color: 'text.secondary',
              }}
            />
            <Box sx={{ marginRight: '8px' }}>{sectionIcon}</Box>
            <Typography>
              {section.title
                ? section.title
                : `${section.type[0].toLocaleUpperCase()}${section.type.slice(
                    1
                  )} Section ${index + 1}`}
            </Typography>
            <Box
              component="span"
              onClick={handleDeleteClick}
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                opacity: '0.25',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  opacity: '1',
                },
              }}
            >
              <RemoveCircleOutlinedIcon fontSize="small" color="error" />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {section.type === 'accordion' ? (
            <AccordionSectionForm
              section={section as AccordionContentDto}
              sectionIndex={index}
              onChange={updated => onChange(index, updated)}
              onDeleteEntry={(sectionIndex, entryIndex) =>
                onDeleteEntry(index, entryIndex)
              }
            />
          ) : (
            <CopySectionForm
              section={section as CopyContentDto}
              sectionIndex={index}
              onChange={updated => onChange(index, updated)}
              onDeleteEntry={(sectionIndex, entryIndex) =>
                onDeleteEntry(index, entryIndex)
              }
            />
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default DraggableSection;
